import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import { Lang } from '../types';
interface FAQProps {
  lang: Lang;
}
const content = {
  en: {
    title: 'Frequently Asked Questions',
    faqs: [{
      q: 'Do you take walk-ins?',
      a: "No. Barboxa Ink works <strong class='text-studio-text'>appointment only</strong> at Piajas Ink Tattoo Studio to ensure proper design time and a professional session."
    }, {
      q: 'Where exactly is the studio?',
      a: "<strong class='text-studio-text'>Piajas Ink Tattoo Studio</strong><br/>📍 2116 SW 8th St, Miami FL 33135<br/>Exact parking/entry details are shared when your appointment is confirmed."
    }, {
      q: 'How much does it cost?',
      a: "Pricing depends on size, placement, detail level, and session time. I'll send you a <strong class='text-studio-text'>clear price range</strong> after reviewing your request — no surprises."
    }, {
      q: 'Do you do cover-ups or touch-ups?',
      a: "Case by case. Send a clear photo and I'll let you know what's possible in fine line."
    }, {
      q: 'How do I secure my spot?',
      a: "A <strong class='text-studio-text'>deposit is required</strong> to confirm your appointment. This goes toward the total cost of your session."
    }, {
      q: 'How long does a session take?',
      a: "Depends on the piece. I'll give you a <strong class='text-studio-text'>time estimate</strong> when I confirm your quote."
    }, {
      q: 'What should I do before my session?',
      a: 'Come well-rested, hydrated, and with food in your stomach. Wear comfortable clothing that gives access to the tattoo area.'
    }]
  },
  es: {
    title: 'Preguntas frecuentes',
    faqs: [{
      q: '¿Trabajas sin cita?',
      a: "No. Barboxa Ink trabaja <strong class='text-studio-text'>solo con cita</strong> en Piajas Ink Tattoo Studio para asegurar tiempo de diseño y una sesión organizada."
    }, {
      q: '¿Dónde queda el estudio exactamente?',
      a: "<strong class='text-studio-text'>Piajas Ink Tattoo Studio</strong><br/>📍 2116 SW 8th St, Miami FL 33135<br/>Detalles de parqueo y entrada se comparten al confirmar la cita."
    }, {
      q: '¿Cuánto cuesta?',
      a: "El precio depende de tamaño, zona, nivel de detalle y tiempo estimado. Te envío un <strong class='text-studio-text'>rango claro de precio</strong> después de revisar tu solicitud — sin sorpresas."
    }, {
      q: '¿Haces cover-ups o retoques?',
      a: 'Depende del caso. Envíame una foto clara y te digo si es viable en Fine Line.'
    }, {
      q: '¿Cómo aseguro mi cupo?',
      a: "Se requiere un <strong class='text-studio-text'>depósito</strong> para confirmar la cita. Este se descuenta del total de tu sesión."
    }, {
      q: '¿Cuánto dura una sesión?',
      a: "Depende de la pieza. Te doy un <strong class='text-studio-text'>estimado de tiempo</strong> al confirmar tu cotización."
    }, {
      q: '¿Qué debo hacer antes de la sesión?',
      a: 'Llega descansado, hidratado y con algo de comida. Usa ropa cómoda que dé acceso a la zona a tatuar.'
    }]
  }
};
export function FAQSection({
  lang
}: FAQProps) {
  const t = content[lang];
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <section id="faq" className="py-12 md:py-16 bg-studio-bgAlt border-t border-studio-border" data-id="element-81">
      <div className="max-w-3xl mx-auto px-6" data-id="element-82">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-3xl md:text-5xl font-bold text-center mb-16" data-id="element-83">
          {t.title}
        </motion.h2>

        <div className="space-y-4" data-id="element-84">
          {t.faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="border border-studio-border bg-studio-bg overflow-hidden" data-id="element-85">
                <button onClick={() => setOpenIndex(isOpen ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-studio-bgAlt transition-colors" data-id="element-86">
                  <span className="font-playfair text-lg md:text-xl font-semibold text-studio-text pr-8" data-id="element-87">
                    {faq.q}
                  </span>
                  <span className="text-studio-accent shrink-0" data-id="element-88">
                    {isOpen ? <Minus size={20} data-id="element-89" /> : <Plus size={20} data-id="element-90" />}
                  </span>
                </button>

                <AnimatePresence data-id="element-91">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-92">
                      <div className="p-6 pt-0 text-studio-muted leading-relaxed" dangerouslySetInnerHTML={{
                  __html: faq.a
                }} data-id="element-93" />
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}